import React, { useState, useEffect } from 'react';
import { X, Upload, FileText, Trash2, Plus } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ASSET_DOC_CATEGORIES = [
  { value: 'libretto_uso_manutenzione', label: 'Libretto uso e manutenzione' },
  { value: 'certificato_conformita', label: 'Certificato conformità' },
  { value: 'revisione_periodica', label: 'Revisione/Controllo periodico' },
  { value: 'documento_consegna', label: 'Documento di consegna' },
  { value: 'fattura_ddt', label: 'Fattura/DDT acquisto' },
  { value: 'assicurazione', label: 'Assicurazione' },
  { value: 'collaudo', label: 'Collaudo' },
  { value: 'foto_asset', label: 'Foto asset' },
  { value: 'altro', label: 'Altro' }
];

function AssetModal({ asset, onClose }) {
  const [employees, setEmployees] = useState([]);
  const [formData, setFormData] = useState({
    tipo: asset?.tipo || 'mezzo',
    nome: asset?.nome || '',
    descrizione: asset?.descrizione || '',
    marca: asset?.marca || '',
    modello: asset?.modello || '',
    matricola: asset?.matricola || '',
    targa: asset?.targa || '',
    data_acquisto: asset?.data_acquisto ? asset.data_acquisto.split('T')[0] : '',
    stato: asset?.stato || 'operativo',
    assegnato_a: asset?.assegnato_a || '',
    assegnato_a_multipli: asset?.assegnato_a_multipli || [],
    assegnato_a_nomi: asset?.assegnato_a_nomi || [] // For manual text input
  });
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [manualNameInput, setManualNameInput] = useState('');
  const [selectedEmployeeIds, setSelectedEmployeeIds] = useState(
    asset?.assegnato_a_multipli || (asset?.assegnato_a ? [asset.assegnato_a] : [])
  );
  const [manualNames, setManualNames] = useState(asset?.assegnato_a_nomi || []);

  useEffect(() => {
    fetchEmployees();
  }, []);

  const fetchEmployees = async () => {
    try {
      const res = await fetch(`${API_URL}/api/employees`);
      const data = await res.json();
      setEmployees(data);
    } catch (err) {
      console.error('Error fetching employees:', err);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };
  
  // Add employee from dropdown
  const addEmployeeFromDropdown = (empId) => {
    if (empId && !selectedEmployeeIds.includes(empId)) {
      setSelectedEmployeeIds([...selectedEmployeeIds, empId]);
    }
  };
  
  // Remove employee
  const removeEmployee = (empId) => {
    setSelectedEmployeeIds(selectedEmployeeIds.filter(id => id !== empId));
  };
  
  // Add manual name
  const addManualName = () => {
    if (manualNameInput.trim() && !manualNames.includes(manualNameInput.trim())) {
      setManualNames([...manualNames, manualNameInput.trim()]);
      setManualNameInput('');
    }
  };
  
  // Remove manual name
  const removeManualName = (name) => {
    setManualNames(manualNames.filter(n => n !== name));
  };

  const handleAddDocument = () => {
    setDocuments([
      ...documents,
      {
        id: Date.now(),
        categoria: 'libretto_uso_manutenzione',
        titolo: '',
        descrizione: '',
        data_scadenza: '',
        file: null
      }
    ]);
  };

  const handleDocumentChange = (id, field, value) => {
    setDocuments(
      documents.map((doc) =>
        doc.id === id ? { ...doc, [field]: value } : doc
      )
    );
  };

  const handleDocumentFileChange = (id, file) => {
    if (file) {
      // Check file type
      const allowedTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'];
      if (!allowedTypes.includes(file.type)) {
        setError('Solo file PDF, JPG o PNG sono permessi');
        return;
      }
      // Check file size (12MB max)
      if (file.size > 12 * 1024 * 1024) {
        setError('Il file non può superare i 12MB');
        return;
      }
      setError('');
      setDocuments(
        documents.map((doc) => (doc.id === id ? { ...doc, file } : doc))
      );
    }
  };

  const handleRemoveDocument = (id) => {
    setDocuments(documents.filter((doc) => doc.id !== id));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.nome) {
      setError('Il nome è obbligatorio');
      return;
    }

    // Validate documents
    for (const doc of documents) {
      if (!doc.titolo || !doc.file) {
        setError('Ogni documento deve avere un titolo e un file');
        return;
      }
    }

    try {
      setLoading(true);
      setError('');
      const token = localStorage.getItem('token');

      const payload = { ...formData };

      // Convert date strings to ISO format or remove if empty
      if (payload.data_acquisto && payload.data_acquisto.trim() !== '') {
        payload.data_acquisto = new Date(payload.data_acquisto).toISOString();
      } else {
        delete payload.data_acquisto;
      }
      
      // Handle multiple assignments
      payload.assegnato_a_multipli = selectedEmployeeIds;
      payload.assegnato_a_nomi = manualNames;
      
      // Keep backward compatibility - use first selected employee as primary
      if (selectedEmployeeIds.length > 0) {
        payload.assegnato_a = selectedEmployeeIds[0];
      } else {
        delete payload.assegnato_a;
      }

      // Add assignment date if assigning to employee
      if ((selectedEmployeeIds.length > 0 || manualNames.length > 0) && !asset?.assegnato_a) {
        payload.data_assegnazione = new Date().toISOString();
      }

      const url = asset
        ? `${API_URL}/api/assets/${asset._id}`
        : `${API_URL}/api/assets`;

      const method = asset ? 'PUT' : 'POST';

      const res = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      if (!res.ok) {
        throw new Error('Errore nel salvataggio');
      }

      const createdAsset = await res.json();

      // Upload documents if any
      if (documents.length > 0 && !asset) {
        for (const doc of documents) {
          // Create compliance item
          const compliancePayload = {
            tipo: 'documento_asset',
            titolo: doc.titolo,
            descrizione: doc.descrizione,
            employee_id: 'asset_' + createdAsset._id,
            data_rilascio: new Date().toISOString(),
            data_scadenza: doc.data_scadenza
              ? new Date(doc.data_scadenza).toISOString()
              : new Date('2099-12-31').toISOString(),
            stato: 'attivo',
            categoria: doc.categoria,
            numero_protocollo: ''
          };

          const compRes = await fetch(`${API_URL}/api/compliance`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json'
            },
            body: JSON.stringify(compliancePayload)
          });

          if (compRes.ok) {
            // Upload file
            const compData = await compRes.json();
            const fileFormData = new FormData();
            fileFormData.append('file', doc.file);
            fileFormData.append('entity_type', 'asset');
            fileFormData.append('entity_id', createdAsset._id);
            fileFormData.append('storage_type', 'filesystem');
            fileFormData.append('categoria', doc.categoria);
            fileFormData.append('descrizione', doc.titolo);

            await fetch(`${API_URL}/api/upload`, {
              method: 'POST',
              headers: {
                'Authorization': `Bearer ${token}`
              },
              body: fileFormData
            });
          }
        }
      }

      onClose(true);
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto"
      data-testid="asset-modal"
    >
      <div className="bg-card rounded-lg max-w-4xl w-full my-8">
        <div className="sticky top-0 bg-card border-b border-border px-6 py-4 flex items-center justify-between rounded-t-lg">
          <h2 className="text-xl font-semibold">
            {asset ? 'Modifica Asset' : 'Nuovo Asset'}
          </h2>
          <button
            onClick={() => onClose(false)}
            className="btn btn-secondary"
            data-testid="close-modal"
          >
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 max-h-[calc(100vh-200px)] overflow-y-auto">
          {error && (
            <div className="bg-destructive/10 text-destructive px-4 py-3 rounded-lg mb-4">
              {error}
            </div>
          )}

          <div className="space-y-6">
            {/* Basic Info Section */}
            <div>
              <h3 className="text-lg font-semibold mb-4">Informazioni Base</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="form-group">
                  <label className="form-label">Tipo *</label>
                  <select
                    name="tipo"
                    className="form-input"
                    value={formData.tipo}
                    onChange={handleChange}
                    required
                    data-testid="input-tipo"
                  >
                    <option value="mezzo">Mezzo</option>
                    <option value="attrezzatura">Attrezzatura</option>
                  </select>
                </div>

                <div className="form-group">
                  <label className="form-label">Nome *</label>
                  <input
                    type="text"
                    name="nome"
                    className="form-input"
                    value={formData.nome}
                    onChange={handleChange}
                    required
                    placeholder="Es: Furgone aziendale, Trapano"
                    data-testid="input-nome"
                  />
                </div>

                <div className="form-group md:col-span-2">
                  <label className="form-label">Descrizione</label>
                  <textarea
                    name="descrizione"
                    className="form-input form-textarea"
                    value={formData.descrizione}
                    onChange={handleChange}
                    rows={3}
                    data-testid="input-descrizione"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Marca</label>
                  <input
                    type="text"
                    name="marca"
                    className="form-input"
                    value={formData.marca}
                    onChange={handleChange}
                    data-testid="input-marca"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Modello</label>
                  <input
                    type="text"
                    name="modello"
                    className="form-input"
                    value={formData.modello}
                    onChange={handleChange}
                    data-testid="input-modello"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Matricola/Seriale</label>
                  <input
                    type="text"
                    name="matricola"
                    className="form-input"
                    value={formData.matricola}
                    onChange={handleChange}
                    data-testid="input-matricola"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Targa (per mezzi)</label>
                  <input
                    type="text"
                    name="targa"
                    className="form-input"
                    value={formData.targa}
                    onChange={handleChange}
                    data-testid="input-targa"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Data Acquisto</label>
                  <input
                    type="date"
                    name="data_acquisto"
                    className="form-input"
                    value={formData.data_acquisto}
                    onChange={handleChange}
                    data-testid="input-data-acquisto"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Stato</label>
                  <select
                    name="stato"
                    className="form-input"
                    value={formData.stato}
                    onChange={handleChange}
                    data-testid="input-stato"
                  >
                    <option value="operativo">Operativo</option>
                    <option value="manutenzione">In Manutenzione</option>
                    <option value="fuori_servizio">Fuori Servizio</option>
                  </select>
                </div>

                <div className="form-group">
                  <label className="form-label">Assegna a Dipendenti</label>
                  
                  {/* Selected employees list */}
                  {selectedEmployeeIds.length > 0 && (
                    <div className="flex flex-wrap gap-2 mb-2">
                      {selectedEmployeeIds.map(empId => {
                        const emp = employees.find(e => e._id === empId);
                        return emp ? (
                          <span key={empId} className="badge badge-primary flex items-center gap-1">
                            {emp.nome} {emp.cognome}
                            <button
                              type="button"
                              onClick={() => removeEmployee(empId)}
                              className="ml-1 hover:text-destructive"
                            >
                              <X size={12} />
                            </button>
                          </span>
                        ) : null;
                      })}
                    </div>
                  )}
                  
                  {/* Manual names list */}
                  {manualNames.length > 0 && (
                    <div className="flex flex-wrap gap-2 mb-2">
                      {manualNames.map((name, idx) => (
                        <span key={idx} className="badge badge-secondary flex items-center gap-1">
                          {name} (manuale)
                          <button
                            type="button"
                            onClick={() => removeManualName(name)}
                            className="ml-1 hover:text-destructive"
                          >
                            <X size={12} />
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                  
                  {/* Dropdown to add from list */}
                  <select
                    className="form-input mb-2"
                    value=""
                    onChange={(e) => {
                      addEmployeeFromDropdown(e.target.value);
                      e.target.value = '';
                    }}
                    data-testid="input-assegnato-a"
                  >
                    <option value="">+ Seleziona dipendente dalla lista...</option>
                    {employees.filter(emp => !selectedEmployeeIds.includes(emp._id)).map((emp) => (
                      <option key={emp._id} value={emp._id}>
                        {emp.nome} {emp.cognome}
                      </option>
                    ))}
                  </select>
                  
                  {/* Manual input */}
                  <div className="flex gap-2">
                    <input
                      type="text"
                      className="form-input flex-1"
                      placeholder="Oppure inserisci nome manualmente..."
                      value={manualNameInput}
                      onChange={(e) => setManualNameInput(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addManualName())}
                    />
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={addManualName}
                      disabled={!manualNameInput.trim()}
                    >
                      Aggiungi
                    </button>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    Puoi assegnare a più persone selezionandole dalla lista o inserendo il nome manualmente
                  </p>
                </div>
              </div>
            </div>

            {/* Documents Section */}
            {!asset && (
              <div className="border-t border-border pt-6">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="text-lg font-semibold">Documenti</h3>
                    <p className="text-sm text-muted-foreground">
                      Carica libretti, certificati, foto (opzionale)
                    </p>
                  </div>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={handleAddDocument}
                    data-testid="add-document"
                  >
                    <Plus size={20} />
                    Aggiungi Documento
                  </button>
                </div>

                {documents.length > 0 && (
                  <div className="space-y-4">
                    {documents.map((doc, index) => (
                      <div
                        key={doc.id}
                        className="border border-border rounded-lg p-4"
                        data-testid={`document-${index}`}
                      >
                        <div className="flex items-start justify-between mb-3">
                          <h4 className="font-semibold">Documento #{index + 1}</h4>
                          <button
                            type="button"
                            className="text-destructive hover:text-destructive/80"
                            onClick={() => handleRemoveDocument(doc.id)}
                            data-testid={`remove-document-${index}`}
                          >
                            <Trash2 size={20} />
                          </button>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          <div className="form-group">
                            <label className="form-label text-sm">Tipo *</label>
                            <select
                              className="form-input"
                              value={doc.categoria}
                              onChange={(e) =>
                                handleDocumentChange(doc.id, 'categoria', e.target.value)
                              }
                              required
                            >
                              {ASSET_DOC_CATEGORIES.map((cat) => (
                                <option key={cat.value} value={cat.value}>
                                  {cat.label}
                                </option>
                              ))}
                            </select>
                          </div>

                          <div className="form-group">
                            <label className="form-label text-sm">Titolo *</label>
                            <input
                              type="text"
                              className="form-input"
                              value={doc.titolo}
                              onChange={(e) =>
                                handleDocumentChange(doc.id, 'titolo', e.target.value)
                              }
                              placeholder="Es: Libretto 2026"
                              required
                            />
                          </div>

                          <div className="form-group md:col-span-2">
                            <label className="form-label text-sm">Descrizione</label>
                            <input
                              type="text"
                              className="form-input"
                              value={doc.descrizione}
                              onChange={(e) =>
                                handleDocumentChange(doc.id, 'descrizione', e.target.value)
                              }
                              placeholder="Note aggiuntive..."
                            />
                          </div>

                          <div className="form-group">
                            <label className="form-label text-sm">Data Scadenza</label>
                            <input
                              type="date"
                              className="form-input"
                              value={doc.data_scadenza}
                              onChange={(e) =>
                                handleDocumentChange(doc.id, 'data_scadenza', e.target.value)
                              }
                            />
                          </div>

                          <div className="form-group">
                            <label className="form-label text-sm">File *</label>
                            <div className="relative">
                              <input
                                type="file"
                                accept=".pdf,.jpg,.jpeg,.png"
                                onChange={(e) =>
                                  handleDocumentFileChange(doc.id, e.target.files?.[0])
                                }
                                className="form-input"
                                required
                              />
                              {doc.file && (
                                <p className="text-xs text-muted-foreground mt-1">
                                  {doc.file.name} ({(doc.file.size / 1024).toFixed(0)} KB)
                                </p>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="flex gap-3 justify-end mt-6 pt-6 border-t border-border">
            <button
              type="button"
              onClick={() => onClose(false)}
              className="btn btn-secondary"
              disabled={loading}
            >
              Annulla
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={loading}
              data-testid="submit-button"
            >
              {loading ? 'Salvataggio...' : asset ? 'Aggiorna' : 'Crea Asset'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default AssetModal;
